import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Service } from "@/types";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/lib/auth";
import { useEffect } from "react";
import { useSessionLogsStore } from "../../lib/stores/sessionLogs";
import { ServicePriceDialog } from "./ServicePriceDialog";

interface ServiceSelectionDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  services: Service[];
  onConfirm: (selectedServices: Service[]) => void;
}

export const ServiceSelectionDialog = ({ 
  isOpen, 
  setIsOpen, 
  services, 
  onConfirm 
}: ServiceSelectionDialogProps) => {
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [isPriceDialogOpen, setIsPriceDialogOpen] = useState(false);
  const { toast } = useToast();

  // Get unique categories from services
  const categories = Array.from(new Set(services.map(s => s.category ?? "").map(cat => cat || "Uncategorized")));
  categories.sort();

  const handleServiceToggle = (service: Service) => {
    setSelectedServices(current => {
      const isSelected = current.some(s => s.id === service.id);
      if (isSelected) {
        return current.filter(s => s.id !== service.id);
      } else {
        return [...current, service];
      }
    });
  };

  const handleQuickConfirm = () => {
    if (selectedServices.length === 0) {
      toast({
        title: "No services selected",
        description: "Please select at least one service",
        variant: "destructive"
      });
      return;
    }

    // Add services with original prices
    onConfirm(selectedServices);
    setSelectedServices([]);
    setIsOpen(false);
  };

  const handleModifyPrices = () => {
    if (selectedServices.length === 0) {
      toast({
        title: "No services selected",
        description: "Please select at least one service",
        variant: "destructive"
      });
      return;
    }

    // Open price modification dialog
    setIsPriceDialogOpen(true);
  };

  const handlePriceDialogConfirm = (servicesWithPrices: any[]) => {
    // Convert back to Service array with modified prices for the original onConfirm
    const modifiedServices = servicesWithPrices.map(item => ({
      ...item.service,
      price: item.modifiedPrice
    }));
    
    onConfirm(modifiedServices);
    setSelectedServices([]);
    setIsOpen(false);
    setIsPriceDialogOpen(false);
  };

  const handleCancel = () => {
    setSelectedServices([]);
    setIsOpen(false);
  };

  // Filter by category and search
  const filteredServices = services.filter(service => {
    const serviceCategory = service.category || "Uncategorized";
    const matchesCategory = category === "All" || serviceCategory === category;
    const matchesSearch = service.name.toLowerCase().includes(search.toLowerCase()) ||
      (service.description && service.description.toLowerCase().includes(search.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Services</DialogTitle>
          <DialogDescription>
            Select services to add for this patient
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 mb-2">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-40">
              <SelectValue>{category === "All" ? "All Categories" : category}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="Search services..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full"
            autoFocus
          />
        </div>
        <ScrollArea className="h-[300px] pr-4">
          <div className="space-y-4 mt-4">
            {filteredServices.length > 0 ? (
              filteredServices.map((service) => (
                <div key={service.id} className="flex items-center space-x-4 p-2 border rounded-md">
                  <Checkbox
                    id={`service-${service.id}`}
                    checked={selectedServices.some(s => s.id === service.id)}
                    onCheckedChange={() => handleServiceToggle(service)}
                  />
                  <div className="flex-1">
                    <label htmlFor={`service-${service.id}`} className="text-sm font-medium cursor-pointer">
                      {service.name}
                    </label>
                    <p className="text-xs text-muted-foreground">{service.description}</p>
                  </div>
                  <div className="text-sm font-semibold">
                    ₹{service.price.toFixed(2)}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No services found
              </p>
            )}
          </div>
        </ScrollArea>
        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            variant="outline" 
            onClick={handleQuickConfirm}
            className="bg-green-600 hover:bg-green-700 text-white border-green-600 hover:border-green-700"
          >
            Confirm
          </Button>
          <Button onClick={handleModifyPrices}>
            Modify Prices
          </Button>
        </div>
      </DialogContent>
      
      {/* Price Modification Dialog */}
      <ServicePriceDialog
        isOpen={isPriceDialogOpen}
        setIsOpen={setIsPriceDialogOpen}
        selectedServices={selectedServices}
        onConfirm={handlePriceDialogConfirm}
      />
    </Dialog>
  );
};

export function UserSessionLogs() {
  const { logs, loading, error, fetchLogs } = useSessionLogsStore();

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading session logs.</div>;

  return (
    <table>
      <thead>
        <tr>
          <th>User</th>
          <th>Role</th>
          <th>Action</th>
          <th>Details</th>
          <th>Time</th>
          <th>IP</th>
          <th>Device</th>
        </tr>
      </thead>
      <tbody>
        {logs.map(log => (
          <tr key={log.id}>
            <td>{log.userName}</td>
            <td>{log.userRole}</td>
            <td>{log.action}</td>
            <td>{log.details}</td>
            <td>{new Date(log.timestamp).toLocaleString()}</td>
            <td>{log.ipAddress}</td>
            <td>{log.deviceInfo}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
